import { useState } from 'react';
import { Calendar, MapPin, Clock, Package, Phone, MessageCircle, Sparkles, Star, X, Send, Ticket, CalendarClock } from 'lucide-react';
import { useBookings, useCourtBookings, useCoachBookings, useMySessionCredits } from '../../hooks/useAPI';
import ReviewModal from './ReviewModal';
import CancelBookingModal from './CancelBookingModal';
import RescheduleBookingModal from './RescheduleBookingModal';
import TransferCreditModal from './TransferCreditModal';
import type { ReviewTarget } from '../../hooks/useAPI';

interface OrdersScreenProps {
  onNavigate: (screen: any) => void;
}

export default function OrdersScreen({ onNavigate }: OrdersScreenProps) {
  const { data: bookings, isLoading } = useBookings();
  const { data: courtBookings, refetch: refetchCourt } = useCourtBookings();
  const { data: coachBookings, refetch: refetchCoach } = useCoachBookings();
  const { data: credits, refetch: refetchCredits } = useMySessionCredits();
  const courtRows = (courtBookings as any[]) || [];
  const coachRows = (coachBookings as any[]) || [];
  const creditRows = (credits as any[]) || [];
  const [reviewTarget, setReviewTarget] = useState<{
    target: ReviewTarget;
    title: string;
    subtitle: string;
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<
    | { kind: 'court' | 'coach'; bookingId: string; reference: string; startAt: string;
        totalAmount: number; currency: string; withCredit?: boolean; clubId?: string }
    | null
  >(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<
    | { kind: 'court' | 'coach'; bookingId: string; reference: string; startAt: string;
        endAt?: string; sessionMinutes?: number }
    | null
  >(null);
  const [transferTarget, setTransferTarget] = useState<
    | { creditId: string; remaining: number; packageName: string }
    | null
  >(null);

  const reload = () => {
    (refetchCourt as any)?.();
    (refetchCoach as any)?.();
    (refetchCredits as any)?.();
    // useQuery refetch is a no-op in our implementation; full reload works.
    location.reload();
  };
  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 py-4">
        <h1 className="text-2xl">My Bookings</h1>
        <p className="text-sm text-muted-foreground">Track your rental history</p>
      </div>

      <div className="px-6 py-6 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-secondary rounded-2xl p-5 h-48 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {courtRows.length > 0 && (
              <div>
                <h3 className="text-base mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#1A6B4F]" />
                  Court bookings
                </h3>
                {courtRows.map((b: any) => {
                  const start = new Date(b.start_at);
                  const end = new Date(b.end_at);
                  return (
                    <div key={b.id} className="bg-card border border-border rounded-2xl p-4 mb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className={`inline-block px-2.5 py-0.5 text-xs rounded-full mb-1 capitalize ${
                            b.status === 'confirmed'
                              ? 'bg-[#D4F04A]/20 text-[#1A6B4F]'
                              : b.status === 'pending_payment'
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-secondary text-muted-foreground'
                          }`}>
                            {b.status.replace('_', ' ')}
                          </div>
                          <div className="text-sm font-medium">{b.clubs?.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {b.courts?.name} · {b.courts?.surface}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{b.booking_reference}</div>
                          <div className="text-sm text-[#1A6B4F] mt-0.5">
                            {b.currency} {Number(b.total_amount).toFixed(0)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        <span className="mx-1">·</span>
                        <Clock className="w-4 h-4" />
                        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        {['confirmed', 'pending_payment'].includes(b.status) && start > new Date() && (
                          <>
                            <button
                              onClick={() => setRescheduleTarget({
                                kind: 'court', bookingId: b.id, reference: b.booking_reference,
                                startAt: b.start_at, endAt: b.end_at,
                              })}
                              className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                            >
                              <CalendarClock className="w-3.5 h-3.5" />
                              Reschedule
                            </button>
                            <button
                              onClick={() => setCancelTarget({
                                kind: 'court', bookingId: b.id, reference: b.booking_reference,
                                startAt: b.start_at, totalAmount: Number(b.total_amount),
                                currency: b.currency, clubId: b.clubs?.id,
                              })}
                              className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                            >
                              <X className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                          </>
                        )}
                        {['confirmed', 'completed'].includes(b.status) && end <= new Date() && (
                          <button
                            onClick={() => setReviewTarget({
                              target: {
                                courtBookingId: b.id,
                                courtId: b.courts?.id,
                                clubId: b.clubs?.id,
                              },
                              title: `Rate ${b.clubs?.name ?? 'the court'}`,
                              subtitle: b.courts?.name,
                            })}
                            className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                          >
                            <Star className="w-3.5 h-3.5" />
                            Leave a review
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {coachRows.length > 0 && (
              <div>
                <h3 className="text-base mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#1A6B4F]" />
                  Coaching lessons
                </h3>
                {coachRows.map((b: any) => {
                  const start = new Date(b.scheduled_at);
                  const end = new Date(b.ends_at);
                  return (
                    <div key={b.id} className="bg-card border border-border rounded-2xl p-4 mb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className={`inline-block px-2.5 py-0.5 text-xs rounded-full mb-1 capitalize ${
                            b.status === 'confirmed'
                              ? 'bg-[#D4F04A]/20 text-[#1A6B4F]'
                              : b.status === 'pending_payment'
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-secondary text-muted-foreground'
                          }`}>
                            {b.status.replace('_', ' ')}
                          </div>
                          <div className="text-sm font-medium">{b.coaches?.display_name}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {b.lesson_type.replace('_', '-')} · {b.environment}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{b.booking_reference}</div>
                          <div className="text-sm text-[#1A6B4F] mt-0.5">
                            {b.currency} {Number(b.total_amount).toFixed(0)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        <span className="mx-1">·</span>
                        <Clock className="w-4 h-4" />
                        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        {['confirmed', 'pending_payment'].includes(b.status) && start > new Date() && (
                          <>
                            <button
                              onClick={() => setRescheduleTarget({
                                kind: 'coach', bookingId: b.id, reference: b.booking_reference,
                                startAt: b.scheduled_at, sessionMinutes: b.session_minutes,
                              })}
                              className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                            >
                              <CalendarClock className="w-3.5 h-3.5" />
                              Reschedule
                            </button>
                            <button
                              onClick={() => setCancelTarget({
                                kind: 'coach', bookingId: b.id, reference: b.booking_reference,
                                startAt: b.scheduled_at, totalAmount: Number(b.total_amount),
                                currency: b.currency,
                                withCredit: !!b.session_credit_id,
                                clubId: b.clubs?.id,
                              })}
                              className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                            >
                              <X className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                          </>
                        )}
                        {['confirmed', 'completed'].includes(b.status) && end <= new Date() && (
                          <button
                            onClick={() => setReviewTarget({
                              target: { coachBookingId: b.id, coachId: b.coaches?.id },
                              title: `Rate ${b.coaches?.display_name ?? 'your coach'}`,
                              subtitle: `${b.lesson_type.replace('_', '-')} lesson`,
                            })}
                            className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                          >
                            <Star className="w-3.5 h-3.5" />
                            Leave a review
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {creditRows.length > 0 && (
              <div>
                <h3 className="text-base mb-3 flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-[#1A6B4F]" />
                  My packages
                </h3>
                {creditRows.map((c: any) => {
                  const pkg = c.session_packages;
                  const packageName = pkg?.name
                    ?? `${pkg?.coaches?.display_name ?? 'Coach'} · ${pkg?.lesson_type?.replace('_','-') ?? ''}`;
                  const expires = new Date(c.expires_at);
                  return (
                    <div key={c.id} className="bg-card border border-border rounded-2xl p-4 mb-3">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{packageName}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {c.sessions_remaining} / {c.sessions_purchased} sessions remaining
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Expires {expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => setTransferTarget({
                            creditId: c.id,
                            remaining: c.sessions_remaining,
                            packageName,
                          })}
                          className="h-9 px-3 rounded-lg border border-border hover:bg-secondary text-xs flex items-center gap-1"
                        >
                          <Send className="w-3.5 h-3.5" />
                          Transfer to a friend
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {bookings && bookings.length > 0 && (
            <div>
              <h3 className="text-base mb-3">Upcoming</h3>
              {bookings
                ?.filter((b: any) => ['confirmed', 'preparing', 'in_transit', 'delivered', 'active'].includes(b.status))
                .map((booking: any) => (
              <div key={booking.id} className="bg-card border border-border rounded-2xl p-5 mb-3">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="inline-block px-3 py-1 bg-[#D4F04A]/20 text-[#1A6B4F] rounded-full text-xs mb-2 capitalize">
                      {booking.status.replace('_', ' ')}
                    </div>
                    <div className="text-xl text-[#1A6B4F]">{booking.booking_reference}</div>
                  </div>
                  <div className="text-3xl">🎾</div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.start_date}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.start_time} - {booking.end_time}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.customer_addresses?.full_address || 'Self Pickup'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.machines?.name} - {booking.rental_packages?.name}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Paid</div>
                    <div className="text-lg text-[#1A6B4F]">AED {booking.total_amount}</div>
                  </div>
                  <div className="flex gap-2">
                    <button className="h-10 px-4 rounded-xl bg-secondary hover:bg-muted transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">Call</span>
                    </button>
                    <button className="h-10 px-4 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">Chat</span>
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button className="h-10 rounded-xl border border-border hover:bg-secondary transition-colors text-sm">
                    View Details
                  </button>
                  <button className="h-10 rounded-xl border border-border hover:bg-secondary transition-colors text-sm">
                    Extend Rental
                  </button>
                </div>
              </div>
            )) || (
              <div className="text-center text-muted-foreground py-4">No upcoming bookings</div>
            )}
            </div>
            )}

            {bookings && bookings.some((b: any) => ['completed', 'cancelled'].includes(b.status)) && (
            <div>
              <h3 className="text-base mb-3">Past Bookings</h3>
              {bookings
                ?.filter((b: any) => ['completed', 'cancelled'].includes(b.status))
                .map((booking: any) => (
              <div key={booking.id} className="bg-card border border-border rounded-2xl p-5 mb-3 opacity-75">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="inline-block px-3 py-1 bg-secondary rounded-full text-xs mb-2 capitalize">
                      {booking.status.replace('_', ' ')}
                    </div>
                    <div className="text-lg">{booking.booking_reference}</div>
                  </div>
                  <div className="text-3xl opacity-50">🎾</div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.start_date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.customer_addresses?.full_address || 'Self Pickup'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.machines?.name} - {booking.rental_packages?.name}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="text-base">AED {booking.total_amount}</div>
                  </div>
                  <div className="flex gap-2">
                    <button className="h-10 px-4 rounded-xl border border-border hover:bg-secondary transition-colors text-sm">
                      Rebook
                    </button>
                    <button className="h-10 px-4 rounded-xl border border-border hover:bg-secondary transition-colors text-sm">
                      Receipt
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>
            )}

            {courtRows.length === 0
              && coachRows.length === 0
              && creditRows.length === 0
              && (!bookings || bookings.length === 0) && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎾</div>
                <h3 className="text-lg mb-2">No bookings yet</h3>
                <p className="text-muted-foreground mb-6">
                  Book a court, schedule a lesson, or grab a ball machine to get started.
                </p>
                <button
                  onClick={() => onNavigate('home')}
                  className="h-12 px-6 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors"
                >
                  Explore
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {reviewTarget && (
        <ReviewModal
          target={reviewTarget.target}
          title={reviewTarget.title}
          subtitle={reviewTarget.subtitle}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => setReviewTarget(null)}
        />
      )}

      {cancelTarget && (
        <CancelBookingModal
          kind={cancelTarget.kind}
          bookingId={cancelTarget.bookingId}
          bookingReference={cancelTarget.reference}
          startAt={cancelTarget.startAt}
          totalAmount={cancelTarget.totalAmount}
          currency={cancelTarget.currency}
          withCredit={cancelTarget.withCredit}
          clubId={cancelTarget.clubId}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); reload(); }}
        />
      )}

      {rescheduleTarget && (
        <RescheduleBookingModal
          kind={rescheduleTarget.kind}
          bookingId={rescheduleTarget.bookingId}
          bookingReference={rescheduleTarget.reference}
          startAt={rescheduleTarget.startAt}
          endAt={rescheduleTarget.endAt}
          sessionMinutes={rescheduleTarget.sessionMinutes}
          onClose={() => setRescheduleTarget(null)}
          onDone={() => { setRescheduleTarget(null); reload(); }}
        />
      )}

      {transferTarget && (
        <TransferCreditModal
          creditId={transferTarget.creditId}
          sessionsRemaining={transferTarget.remaining}
          packageName={transferTarget.packageName}
          onClose={() => setTransferTarget(null)}
          onDone={() => { setTransferTarget(null); reload(); }}
        />
      )}
    </div>
  );
}
